/**
 * The sign-in screen.
 *
 * One card floating on a lavender page: the form on the left, the illustration
 * bleeding to the card's right edge. Below md the illustration is gone rather
 * than stacked — it is decoration, and a phone should open on the two fields
 * somebody came here to fill.
 *
 * The palette here is violet and the rest of the application is indigo. That
 * is deliberate and it is the only screen it applies to: this one is seen
 * signed out, where there is no shell around it to match. Written as literal
 * hex rather than through the theme for the same reason — Tailwind v4's own
 * palette is defined in OKLCH, so `violet-600` is near #7C3AED but not equal
 * to it, and a brand colour that is nearly right is worse than one that says
 * what it is.
 *
 * What is *not* cosmetic, and must survive any redesign of this file:
 *
 * * the open-shift refusal and the way past it — without it a browser that
 *   died mid-shift locks its owner out until a nightly job;
 * * the reason the last session ended, read once and shown here;
 * * where to go when a password fails, since this system has no self-service
 *   reset and an error with no way forward is a dead end.
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Loader2, Lock, Mail } from 'lucide-react';
import axios from 'axios';
import { errorMessage, takeSignOutReason } from '../api/client';
import { useAuth } from '../store/auth';
import { MaskedInput } from '../components/MaskedInput';

/**
 * Deliberately permissive: something, an @, something, a dot, something.
 *
 * The server owns the real answer — the login payload is an EmailStr and a
 * malformed address is refused there. This exists only to catch the typo
 * before a round trip, and a stricter pattern here would start rejecting
 * addresses the API would have accepted.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The pill field, and the focus treatment that is the same on both of them.
 *
 * `pl-12` is the room the leading icon sits in. MaskedInput adds its own
 * `pr-10` for the reveal button, so the password field ends up padded on both
 * sides and the email field only on the left, which is what each needs.
 */
const FIELD = `w-full rounded-full border-0 bg-[#F4F5FA] py-3 pl-12 pr-4 text-sm
  text-[#1F2937] transition-[box-shadow,background-color] duration-200
  placeholder:text-[#9AA1B1] focus:bg-[#F9F5FF]
  focus:shadow-[0_0_12px_rgba(124,58,237,0.4)] focus:outline-none`;

/** Scale sits on the wrapper, not the input.
 *
 * The password field carries an absolutely positioned reveal button, and
 * growing the input underneath it would leave the eye behind. Scaling the
 * thing they share moves both. The wrapper is also what the leading icon is
 * positioned against, so it has to be `relative`. */
const FIELD_WRAP = 'relative transition-transform duration-200 focus-within:scale-[1.01]';

const ICON = 'pointer-events-none absolute left-4 top-1/2 z-10 h-[18px] w-[18px] -translate-y-1/2 text-[#6B7280]';

type FieldErrors = { email?: string; password?: string };

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuth((s) => s.login);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /** Per-field, from the browser-side check. Distinct from `error` below,
   *  which is whatever the server said about an attempt that was actually
   *  made. */
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Kept, and not sent.
   *
   * The session length is a server setting (REFRESH_TOKEN_EXPIRE_DAYS, 14 at
   * the time of writing) and the login payload forbids unknown fields, so
   * posting this would turn every sign-in into a 422. The control is here
   * because the design asks for it; wiring it means giving the API somewhere
   * to put it first. The label says "Remember me" and not a number of days
   * precisely because the number is not this screen's to promise.
   */
  const [rememberMe, setRememberMe] = useState(false);
  /** Whether "Forgot Password?" has been asked, and answered in place. */
  const [showRecovery, setShowRecovery] = useState(false);
  /**
   * Why the last session ended, if it ended for a reason worth stating.
   *
   * Read once, on mount, and cleared as it is read — so it shows on the
   * redirect that caused it and does not greet somebody who comes back to
   * /login an hour later. useState's initialiser rather than an effect,
   * because an effect would run twice under StrictMode and the second read
   * would find it already taken.
   */
  const [signedOutReason] = useState(takeSignOutReason);
  /**
   * The open shift blocking this login, if the server named one.
   *
   * Holding it is what turns the refusal into something actionable: a plain
   * error would leave somebody whose browser crashed mid-shift with no way
   * back in short of waiting for a nightly job.
   */
  const [shiftConflict, setShiftConflict] = useState<string | null>(null);

  async function attempt(continueShift: boolean) {
    setError(null);
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password, continueShift);
      const from = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(from, { replace: true });
    } catch (err) {
      // 409 is the open-shift refusal, and the only error offering a way
      // forward. Everything else is a plain failure.
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setShiftConflict(errorMessage(err, 'This account is on shift elsewhere'));
      } else {
        setShiftConflict(null);
        setError(errorMessage(err, 'Could not sign in'));
      }
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    const found: FieldErrors = {};
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) found.email = 'Enter your email id';
    else if (!EMAIL_PATTERN.test(trimmed)) found.email = 'That is not an email address';
    if (!password) found.password = 'Enter your password';

    setErrors(found);
    if (Object.keys(found).length > 0) return;

    void attempt(false);
  }

  return (
    // The page scrolls, not the body — see `overflow: hidden` in index.css. A
    // short window keeps the sign-in button reachable instead of cutting it off.
    <div className="flex h-full items-center overflow-y-auto bg-[#EEF1FB] px-4 py-4 sm:px-6 sm:py-6">
      {/* `my-auto` rather than a centred flex child: with `items-center` alone
          an overflowing card is pinned by its own centring and the top of it
          becomes unreachable, which is the one thing worse than scrolling. */}
      <div
        className="ats-rise mx-auto my-auto flex min-h-[460px] w-full max-w-5xl overflow-hidden
                   rounded-3xl bg-white shadow-[0_24px_80px_-20px_rgba(76,29,149,0.28)]"
      >
        {/* The form column. */}
        <div className="flex w-full flex-col justify-center px-6 py-8 sm:px-9 md:w-[46%] lg:px-12">
          <header>
            <div className="text-2xl leading-none font-extrabold tracking-tight text-[#1F2937]">
              BNB<span className="text-[#7C3AED]"> India</span>
            </div>
            <p className="mt-1.5 text-[10px] font-semibold tracking-[0.18em] text-[#6B7280] uppercase">
              People | Process | Progress
            </p>
          </header>

          <p className="mt-6 text-[11px] font-semibold tracking-[0.22em] text-[#9AA1B1] uppercase">
            Welcome to
          </p>
          <h1 className="mt-1.5 text-[26px] leading-[1.1] font-bold text-[#1F2937] lg:text-[30px]">
            BNB India
          </h1>
          <h2 className="text-[23px] leading-[1.15] font-bold text-[#1F2937] lg:text-[27px]">
            <span className="text-[#7C3AED]">Recruitment</span> Dashboard
          </h2>
          <p className="mt-2 text-sm text-[#6B7280]">
            Sign in to continue and make opportunities happen.
          </p>

          {/* Why the last session ended. Above the form, because it explains
              why this screen is here at all rather than commenting on the
              attempt somebody has not made yet. */}
          {signedOutReason && (
            <div
              className="mt-6 flex gap-2 rounded-2xl bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900 ring-1 ring-amber-200"
              role="status"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{signedOutReason}</p>
            </div>
          )}

          {/* noValidate: the browser's own bubble would pre-empt the inline
              messages below and say something this screen cannot word. */}
          <form onSubmit={onSubmit} noValidate className="mt-5 space-y-3.5">
            <div>
              {/* The placeholder is the label in this design, so the real one
                  is kept for screen readers rather than dropped. A field whose
                  only name disappears the moment you type in it is a field
                  nobody using a reader can re-identify. */}
              <label className="sr-only" htmlFor="email">
                Email Id
              </label>
              <div className={FIELD_WRAP}>
                <Mail className={ICON} aria-hidden="true" />
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  spellCheck={false}
                  placeholder="Email Id"
                  className={`${FIELD} ${
                    errors.email
                      ? 'ring-2 ring-[#DC2626]'
                      : 'ring-1 ring-transparent focus:ring-2 focus:ring-[#7C3AED]'
                  }`}
                  value={email}
                  // Settled on blur rather than on every keystroke: rewriting
                  // the value mid-word moves the caret and fights anyone
                  // typing a capital.
                  onBlur={() => setEmail((v) => v.trim().toLowerCase())}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors((x) => ({ ...x, email: undefined }));
                  }}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  autoFocus
                />
              </div>
              {errors.email && (
                <p id="email-error" className="mt-1.5 pl-4 text-xs text-[#DC2626]">
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <label className="sr-only" htmlFor="password">
                Password
              </label>
              {/* MaskedInput rather than a toggle written again here. It is
                  the same reveal used on the bank and Aadhaar fields, and it
                  already handles the two things a hand-rolled one gets wrong:
                  type="button", so the eye does not submit the form, and an
                  aria-pressed label, so the state is spoken and not only
                  drawn. */}
              <div className={FIELD_WRAP}>
                <Lock className={ICON} aria-hidden="true" />
                <MaskedInput
                  id="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  className={`${FIELD} ${
                    errors.password
                      ? 'ring-2 ring-[#DC2626]'
                      : 'ring-1 ring-transparent focus:ring-2 focus:ring-[#7C3AED]'
                  }`}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) {
                      setErrors((x) => ({ ...x, password: undefined }));
                    }
                  }}
                  aria-invalid={Boolean(errors.password)}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
              </div>
              {errors.password && (
                <p id="password-error" className="mt-1.5 pl-4 text-xs text-[#DC2626]">
                  {errors.password}
                </p>
              )}
            </div>

            <div className="pt-1">
              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-[#6B7280]">
                <input
                  type="checkbox"
                  className="h-[18px] w-[18px] rounded-[5px] border-gray-300 accent-[#7C3AED]"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
            </div>

            {error && (
              <div
                className="space-y-1.5 rounded-2xl bg-red-50 px-3.5 py-3 text-sm text-red-700"
                role="alert"
              >
                <p>{error}</p>
                {/* What to do about it.
                    Passwords are issued by an administrator and there is no
                    email in this system, so a failed sign-in has no
                    self-service way forward — and a screen that only says
                    "Incorrect email or password" leaves people guessing at a
                    password that may have been replaced without them being
                    told. Naming who can fix it, and where, is the difference
                    between a message and a way out. Covers a disabled account
                    too: same people, same screen. */}
                <p className="text-xs text-red-600">
                  Forgotten it, or had it reset? Your administrator or manager
                  can issue a new one from <strong>Team → Users</strong>.
                </p>
              </div>
            )}

            {/* The open-shift refusal, with the way past it.
                Separate from `error` because it is not a dead end: the shift is
                the thing in the way, and carrying it over is a decision this
                person is entitled to make about their own attendance. Without
                this a browser that died mid-shift locks its owner out until a
                nightly job closes the session. */}
            {shiftConflict && (
              <div
                className="space-y-2 rounded-2xl bg-amber-50 px-3.5 py-3 text-sm text-amber-900 ring-1 ring-amber-200"
                role="alert"
              >
                <div className="flex gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>{shiftConflict}</p>
                </div>
                <button
                  type="button"
                  className="btn-ghost w-full text-sm"
                  disabled={busy}
                  onClick={() => void attempt(true)}
                >
                  Continue that shift here
                </button>
                <p className="text-xs text-amber-700">
                  Your shift keeps running, with the same start time. Check-ins
                  carry on from this machine.
                </p>
              </div>
            )}

            {/* The circle and its caption are one button, not a button beside a
                label. Split in two, the control's accessible name would be the
                arrow — which is to say nothing — and the word that explains it
                would not be part of the thing you press. */}
            <div className="pt-1">
              <button
                type="submit"
                disabled={busy}
                className="group mx-auto flex flex-col items-center gap-2 disabled:cursor-not-allowed"
              >
                {/* The hover and press states are dropped while busy rather
                    than overridden. Stacking `group-disabled:group-hover:`
                    compiles to `.group:disabled .group:hover &`, a descendant
                    selector that can never match — dead CSS that reads as a
                    rule. Two whole strings say which state applies. */}
                <span
                  className={`flex h-14 w-14 items-center justify-center rounded-full
                             bg-gradient-to-br from-[#7C3AED] to-[#6D28D9] text-white
                             shadow-[0_8px_20px_rgba(124,58,237,0.3)]
                             transition-[transform,box-shadow] duration-200
                             motion-reduce:transition-none ${
                               busy
                                 ? 'opacity-60'
                                 : `group-hover:scale-[1.06] group-active:scale-[0.94]
                                    group-hover:shadow-[0_12px_28px_rgba(124,58,237,0.45)]
                                    motion-reduce:group-hover:scale-100`
                             }`}
                >
                  {busy ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <ArrowRight className="h-5 w-5" />
                  )}
                </span>
                <span className="text-sm font-medium text-[#1F2937]">
                  {busy ? 'Signing in' : 'Sign In'}
                </span>
              </button>
            </div>
          </form>

          {/* A button, not a link. There is nowhere to send them: this system
              has no self-service reset — POST /auth/reset-password spends a
              token from a link an administrator issues, and there is no email
              to send one with. So the honest answer is the answer itself, said
              in place rather than after a failed attempt. type="button" keeps
              it out of the form it sits under. */}
          <p className="mt-6 text-center text-sm text-[#6B7280]">
            Forgot Password?{' '}
            <button
              type="button"
              aria-expanded={showRecovery}
              onClick={() => setShowRecovery((v) => !v)}
              className="font-medium text-[#7C3AED] underline underline-offset-2 transition-colors hover:text-[#A78BFA]"
            >
              Contact your admin
            </button>
          </p>

          {showRecovery && (
            <p className="mt-2.5 rounded-2xl bg-[#F9F5FF] px-3.5 py-2.5 text-center text-xs text-[#6B7280] ring-1 ring-violet-100">
              Contact your admin or manager to reset it.
            </p>
          )}
        </div>

        {/* Decoration, and marked as such: alt="" keeps a screen reader from
            announcing a laptop to somebody trying to sign in. Hidden outright
            below md rather than stacked — a phone should open on the fields.

            The violet base is what the image is cropped *against*, so a
            viewport that exposes a sliver shows more of the same picture
            rather than a white seam. */}
        <div className="hidden bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] md:block md:w-[54%]">
          {/* object-position 85%, and the artwork is why.
              background-img.png is 1604 × 981 — an aspect of 1.64 — while this
              panel is nearer 1.0, so cover fills the height and keeps only
              about 61% of the width. The laptop sits at 47%–98% of the
              picture. Anchored hard right the window starts at 39% and the
              laptop is jammed against the form; at 85% it starts nearer 33%,
              which leaves the sweep of white the laptop reads against and
              trims the same stacked edges the design trims. Centred would be
              22%–83% and would cut the laptop in half. */}
          <img
            src="/background-img.png"
            alt=""
            className="h-full w-full object-cover object-[85%_50%]"
          />
        </div>
      </div>
    </div>
  );
}
